import { useState, useCallback } from 'react';
import useEsMobile from '../../hooks/useEsMobile';

/** Ancho del sidebar abierto. 200 es el default de antd y es el que venian
 *  usando MainLayout y ClienteLayout hardcodeado en el `marginLeft` del
 *  contenido. PujaLayout usa 220. */
const ANCHO_ABIERTO = 200;

/** Ancho del sidebar colapsado en desktop: quedan los iconos, sin texto.
 *  Tambien es el default de antd. */
const ANCHO_COLAPSADO_DESKTOP = 80;

/* El sidebar abierto en mobile se superpone al contenido (es `position:fixed`),
 * asi que tiene que quedar por encima del Header —que es `sticky` con
 * `zIndex: 1`— y del fondo que lo cierra. Los dos valores viven juntos aca a
 * proposito: separados en dos archivos, el dia que alguien toque uno solo el
 * fondo tapa al sidebar y nadie entiende por que. */
export const Z_FONDO_SIDEBAR = 998;
const Z_SIDER_MOBILE = 999;

/**
 * Estado responsive del sidebar, compartido por los tres layouts.
 *
 * POR QUE UN HOOK Y NO TRES COPIAS: los tres layouts ya tienen triplicado el
 * polling de notificaciones y eso esta anotado como deuda en PENDIENTES. Se
 * vienen dos layouts mas (admin de plataforma y comisionista); meter esta
 * logica copiada seria salir con cinco copias en vez de tres. Los proximos
 * layouts arrancan llamando a este hook y ya nacen responsive.
 *
 * EL PROBLEMA QUE RESUELVE: los `<Sider>` no tenian `breakpoint`, median 200px
 * fijos y el contenido arrancaba con `marginLeft: 200` hardcodeado. En un
 * telefono de 375px al contenido le quedaban 175px de ancho. Ninguna pantalla
 * se ve bien con eso, por mejor hecha que este.
 *
 * COLAPSA EN `lg` (992px). El porque de ese umbral —y su acuerdo con las media
 * queries de `index.css`— vive en `useEsMobile`, que es de donde sale el dato.
 *
 * DOS ESTADOS SEPARADOS, UNO DERIVADO: `collapsed` NO es estado propio, sale de
 * `esMobile`. La alternativa —un solo `collapsed` sincronizado con un
 * `useEffect`— pisa la preferencia del usuario: si colapsaba el sidebar en
 * desktop, achicaba la ventana y la volvia a agrandar, el efecto se lo abria de
 * nuevo. Guardando la preferencia de desktop aparte, volver de mobile la
 * respeta. Estado derivado > estado sincronizado.
 *
 * @param {number} anchoAbierto Ancho del sidebar desplegado (default 200).
 */
export default function useSidebarResponsive(anchoAbierto = ANCHO_ABIERTO) {
  /* La deteccion de mobile se extrajo a `useEsMobile` cuando aparecio el
   * segundo consumidor (`useColumnasResponsive`, para las tablas). Ahi esta
   * documentado por que el breakpoint es `lg` y por que la comparacion va
   * contra `false` explicito. Dos copias de esa logica serian dos lugares
   * donde cambiar el breakpoint — justo lo que el comentario de mas abajo
   * sobre 'una sola fuente de verdad' pide evitar. */
  const esMobile = useEsMobile();

  // Preferencia del usuario en desktop. Sobrevive al paso por mobile A
  // PROPOSITO: es una eleccion, y volver de mobile tiene que respetarla.
  const [colapsadoDesktop, setColapsadoDesktop] = useState(false);
  // En mobile el sidebar es un panel que se abre sobre el contenido, y siempre
  // arranca cerrado: no puede robarle ancho a la pantalla sin que se lo pidan.
  const [abiertoMobile, setAbiertoMobile] = useState(false);

  /* El panel de mobile se cierra al cruzar el breakpoint, en CUALQUIER
   * direccion. Sin esto: tablet en portrait, abris el menu, rotas a landscape
   * —se ignora la bandera, todo bien— y al volver a portrait el sidebar y el
   * fondo negro aparecen SOLOS, sin que nadie los pida.
   *
   * No contradice lo de arriba sobre estado derivado: `colapsadoDesktop` es una
   * PREFERENCIA y hay que preservarla; `abiertoMobile` es estado efimero de un
   * panel, que fuera de mobile no significa nada. Resetearlo no pisa ninguna
   * eleccion porque no hay eleccion que pisar.
   *
   * Y va en fase de render, no en un `useEffect`: el efecto corre DESPUES del
   * paint, asi que al volver a mobile se veria un frame con el sidebar y el
   * fondo negro abiertos antes de que el reset los cierre — el mismo bug, mas
   * corto. React re-renderiza de inmediato sin llegar a pintar el intermedio.
   * Es el patron documentado para ajustar estado cuando cambia una entrada. */
  const [eraMobile, setEraMobile] = useState(esMobile);
  if (eraMobile !== esMobile) {
    setEraMobile(esMobile);
    setAbiertoMobile(false);
  }

  const collapsed = esMobile ? !abiertoMobile : colapsadoDesktop;

  const alternar = useCallback(() => {
    if (esMobile) setAbiertoMobile(a => !a);
    else setColapsadoDesktop(c => !c);
  }, [esMobile]);

  /* Se llama al navegar. En mobile el sidebar tapa el contenido, asi que
   * despues de elegir una opcion tiene que irse solo: quedarse abierto sobre la
   * pantalla recien cargada obliga a un tap de mas. En desktop no hace nada. */
  const cerrarSiEsMobile = useCallback(() => {
    if (esMobile) setAbiertoMobile(false);
  }, [esMobile]);

  return {
    esMobile,
    collapsed,
    alternar,
    cerrarSiEsMobile,

    /* Props para el <Sider>. En mobile colapsa a 0 —desaparece del todo, no
     * deja la banda de iconos.
     *
     * NO lleva el prop `breakpoint` de antd A PROPOSITO. Con `collapsed`
     * controlado, el `handleSetCollapsed` de antd no toca su estado interno
     * (`if (!('collapsed' in props))`), asi que `breakpoint` solo setearia su
     * flag `below` —que existe para dibujar el trigger de ancho cero, y ese ya
     * esta desactivado con `trigger={null}`— y dispararia `onCollapse`, que no
     * le pasamos. Se referencia la FUNCION y no un numero de linea porque
     * `package.json` permite `antd@^5.12.8`: cualquier `npm install` corre las
     * lineas, pero un nombre sobrevive al bump. Seria un
     * segundo `matchMedia` escuchando para nada y, peor, una segunda fuente de
     * verdad sobre "esto es mobile". La unica es `Grid.useBreakpoint`. */
    propsSider: {
      collapsed,
      width: anchoAbierto,
      collapsedWidth: esMobile ? 0 : ANCHO_COLAPSADO_DESKTOP,
    },

    /* Va en el `style` del <Sider>, no en sus props: `zIndex` no es prop de
     * antd y spreadearlo terminaria como atributo invalido en el DOM. Sube
     * sobre el Header (`sticky`, `zIndex: 1`) y sobre el fondo. */
    zIndexSider: esMobile ? Z_SIDER_MOBILE : undefined,

    /* Margen del contenido. En mobile es 0: el sidebar se superpone en vez de
     * empujar, asi que la pantalla usa los 375px completos. */
    marginLeftContenido: esMobile
      ? 0
      : (collapsed ? ANCHO_COLAPSADO_DESKTOP : anchoAbierto),
  };
}
